import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, MoreHorizontal, X } from 'lucide-react'
import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import type { OrganizationSummary, PersonSummary, SourceStatus } from '@bre-hub/contracts'
import { api, ApiError } from '../api'

export function Loading({ label = 'Loading', full = false }: { label?: string; full?: boolean }) {
  const body = (
    <div className="row loading" role="status" style={{ justifyContent: 'center', padding: full ? 0 : 32 }}>
      <span className="spinner" aria-hidden />
      <span className="muted">{label}…</span>
    </div>
  )
  return full ? <div className="center-page">{body}</div> : body
}

// `title={null}` shows the message alone: an action's refusal is one line.
export function ErrorNotice({ error, title }: { error: unknown; title?: string | null }) {
  const message = error instanceof ApiError ? error.message : 'Something went wrong. Try again.'
  const heading = title === null ? null : (title ?? (error instanceof ApiError && error.status === 403 ? 'Not allowed' : 'Could not load this'))
  return (
    <div className="notice notice-bad" role="alert">
      <AlertTriangle size={18} aria-hidden />
      <div>
        {heading && <strong>{heading}</strong>}
        <p>{message}</p>
      </div>
    </div>
  )
}

export function StatusPill({ status }: { status: SourceStatus | 'active' | 'inactive' | 'revoked' | 'expired' }) {
  const tone: Record<string, string> = {
    healthy: 'pill-ok',
    active: 'pill-ok',
    degraded: 'pill-warn',
    expired: 'pill-warn',
    unavailable: 'pill-bad',
    revoked: 'pill-bad',
    inactive: '',
    not_configured: '',
  }
  const label = status.replace('_', ' ')
  return <span className={`pill ${tone[status] ?? ''}`}>{label.charAt(0).toUpperCase() + label.slice(1)}</span>
}

export function Dialog({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  const titleId = useId()
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  return (
    <div className="dialog-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="card dialog" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="card-head">
          <h2 id={titleId}>{title}</h2>
          <button type="button" className="btn btn-quiet" onClick={onClose} aria-label="Close">
            <X size={18} aria-hidden />
          </button>
        </div>
        <div className="card-body">{children}</div>
      </div>
    </div>
  )
}

// Type-ahead over People, for granting access and adding members.
export function PersonPicker({ onPick, onQuery, label = 'Person', autoFocus = true }: { onPick: (person: PersonSummary) => void; onQuery?: (q: string) => void; label?: string; autoFocus?: boolean }) {
  const [q, setQ] = useState('')
  const id = useId()
  const { data, isFetching } = useQuery({ queryKey: ['people', q], queryFn: () => api.people(q), enabled: q.trim().length >= 2 })
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        className="input"
        value={q}
        onChange={(event) => {
          setQ(event.target.value)
          onQuery?.(event.target.value)
        }}
        onKeyDown={(event) => event.key === 'Enter' && event.preventDefault()}
        placeholder="Type a name, email or phone"
        autoComplete="off"
        autoFocus={autoFocus}
      />
      {q.trim().length >= 2 && (
        <ul className="list card" style={{ maxHeight: 220, overflowY: 'auto', boxShadow: 'none' }}>
          {isFetching && !data && <li className="muted">Searching…</li>}
          {data?.people.length === 0 && <li className="muted">No one matches “{q}”</li>}
          {data?.people.map((person) => (
            <li key={person.id}>
              <span>
                {person.displayName} <span className="faint">{person.primaryEmail}</span>
              </span>
              <button type="button" className="btn" onClick={() => onPick(person)}>
                Choose
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// Type-ahead over Organizations.
export function OrganizationSearch({ onPick, onQuery, label = 'Organization' }: { onPick: (organization: OrganizationSummary) => void; onQuery?: (q: string) => void; label?: string }) {
  const [q, setQ] = useState('')
  const id = useId()
  const { data, isFetching } = useQuery({ queryKey: ['organizations', q], queryFn: () => api.organizations(q), enabled: q.trim().length >= 2 })
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        className="input"
        value={q}
        onChange={(event) => {
          setQ(event.target.value)
          onQuery?.(event.target.value)
        }}
        onKeyDown={(event) => event.key === 'Enter' && event.preventDefault()}
        placeholder="Type a name"
        autoComplete="off"
      />
      {q.trim().length >= 2 && (
        <ul className="list card" style={{ maxHeight: 200, overflowY: 'auto', boxShadow: 'none' }}>
          {isFetching && !data && <li className="muted">Searching…</li>}
          {data?.organizations.length === 0 && <li className="muted">No organization matches “{q}”</li>}
          {data?.organizations.map((organization) => (
            <li key={organization.id}>
              <span>{organization.name}</span>
              <button type="button" className="btn" onClick={() => onPick(organization)}>
                Choose
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// A card head's buttons. On a wide screen every action sits in a row; on a
// phone the secondary ones (`more`) fold into a More menu, so a card with
// many tools keeps its title and its main action on one line.
export function CardActions({ more, children }: { more?: ReactNode; children?: ReactNode }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const menuId = useId()
  useEffect(() => {
    if (!open) return
    const onDown = (event: PointerEvent) => ref.current && !ref.current.contains(event.target as Node) && setOpen(false)
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false)
    document.addEventListener('pointerdown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])
  return (
    <div className="row card-actions" ref={ref} data-open={open || undefined}>
      {more && (
        <>
          <button type="button" className="btn btn-quiet card-actions-more" aria-expanded={open} aria-controls={menuId} onClick={() => setOpen((value) => !value)}>
            <MoreHorizontal size={16} aria-hidden /> More
          </button>
          <div className="card-actions-menu" id={menuId} onClick={(event) => (event.target as HTMLElement).closest('a, button') && setOpen(false)}>
            {more}
          </div>
        </>
      )}
      {children}
    </div>
  )
}
